import { createFileRoute } from '@tanstack/react-router';
import { Gift, Search } from 'lucide-react';
import { useState } from 'react';

import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageContainer,
  PageHeader,
} from '@/components/shared';
import { Input } from '@/components/ui/input';
import { useRelationship } from '@/hooks/queries/use-relationship';
import { useUserMe } from '@/hooks/queries/use-user-me';

import { InvitePartnerEmptyState, WishlistItemCard } from './-components';

export const Route = createFileRoute('/(app)/wishlist/partner')({
  component: PartnerWishlistPage,
});

function PartnerWishlistPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserMe();
  const relationshipId = userData?.relationships?.[0]?.id;

  const {
    data: relationshipData,
    isLoading: isLoadingRelationship,
    error: relationshipError,
    refetch,
  } = useRelationship(relationshipId);

  const isLoading = isLoadingUser || isLoadingRelationship;
  const error = userError || relationshipError;

  const partnerWishlist = relationshipData?.partner_wishlist ?? [];
  const hasPartner = !!relationshipData?.relationship?.guest_user_id;
  const inviteToken = relationshipData?.relationship?.invite_token;

  const filteredItems = partnerWishlist.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Carregando lista do parceiro..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState
          title="Erro ao carregar lista"
          message="Não foi possível carregar a wishlist do parceiro. Tente novamente."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  if (!hasPartner) {
    return (
      <PageContainer>
        <div className="space-y-6">
          <PageHeader title="Lista do Parceiro" />
          <InvitePartnerEmptyState inviteToken={inviteToken} />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Lista do Parceiro"
          subtitle={`${partnerWishlist.length} itens`}
        />

        {partnerWishlist.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Buscar presentes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {partnerWishlist.length === 0 ? (
          <EmptyState
            icon={<Gift className="h-6 w-6" />}
            title="Lista vazia"
            description="Seu parceiro ainda não adicionou nenhum item à lista."
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="Nenhum resultado"
            description="Não encontramos itens com esse termo."
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {filteredItems.map((item) => (
              <WishlistItemCard
                key={item.id}
                item={item}
                readOnly
                variant="partner"
              />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
